"use client";

import { useEffect, useState } from "react";
import { useTheme } from "next-themes";

/**
 * Theme Synchronizer Component
 * Syncs theme across main domain and subdomains
 */
export function ThemeSynchronizer() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Apply saved theme on initial mount
  useEffect(() => {
    setMounted(true);

    // Get theme from localStorage first (fastest)
    const savedTheme = localStorage.getItem("portfolio-theme");

    if (savedTheme && resolvedTheme !== savedTheme) {
      setTheme(savedTheme);
    }
  }, [setTheme, resolvedTheme]);

  // Persist theme changes to API and localStorage
  useEffect(() => {
    if (!mounted || !theme || theme === "system") return;

    // Save to localStorage
    try {
      localStorage.setItem("portfolio-theme", theme);
    } catch (e) {
      console.warn("Could not save to localStorage:", e);
    }

    // Sync theme via API (sets cookie on server)
    syncThemeToAPI(theme);

    // Update HTML attribute directly
    if (typeof window !== "undefined") {
      document.documentElement.setAttribute("data-theme", theme);
    }
  }, [theme, mounted]);

  return null;
}

/**
 * Sync theme preference to backend API
 * This ensures theme is saved via Set-Cookie header which has broader support
 */
async function syncThemeToAPI(theme: string): Promise<void> {
  try {
    const response = await fetch("/api/theme", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ theme }),
      credentials: "include", // Important: include credentials for cookie handling
    });

    if (!response.ok) {
      console.warn("Failed to sync theme to API");
    }
  } catch (error) {
    console.warn("Could not sync theme to API:", error);
  }
}
